import { CategoryPayload } from '@ankor-io/common/types/transaction/category'
import { TaxLabelPayload } from '@ankor-io/common/types/transaction/tax'
import { VatCode } from '@ankor-io/common/types/transaction/transaction'
import { Balance } from '@ankor-io/common/types/wallet/balance'

/**
 * Start raw xero types
 */

export type XeroConnection = {
  id: string
  organizationId: string
  accessToken: string
  refreshToken: string
  refreshTokenExpiration: string | null
  tokenExpiration: string
  createdAt: string
  updatedAt: string
  status: XeroConnectionStatus
}

export enum XeroConnectionStatus {
  ACTIVE = 'ACTIVE',
  INACTIVE = 'INACTIVE',
  DELETED = 'DELETED',
}

/** Connection without secrets — safe for Lighthouse/API (matches *Payload convention). */
export type XeroConnectionPayload = Omit<XeroConnection, 'accessToken' | 'refreshToken'>

/** Organization Xero overview for Lighthouse/API. */
export type XeroOrganizationOverview = {
  connectionCount: number
  primaryConnection: XeroConnectionPayload
  latestSync: XeroSyncJobPayload | null
}

export enum XeroRefreshTokenStatus {
  // Refresh token has more than 1 day remaining before expiry.
  VALID = 'VALID',
  // Refresh token will expire within the next day and should be renewed soon.
  EXPIRING_SOON = 'EXPIRING_SOON',
  // Refresh token is no longer usable and the Xero connection must be refreshed.
  EXPIRED = 'EXPIRED',
}

// Xero refresh tokens remain valid for 60 days from issuance.
export const XERO_REFRESH_TOKEN_TTL_DAYS = 60
// Refresh tokens with 1 day or less remaining are surfaced as EXPIRING_SOON.
export const XERO_REFRESH_TOKEN_EXPIRING_SOON_DAYS = 1
// Xero brand colour — also applied as CSS `--xero-branding` (web `main.tsx`, ui-components Storybook `preview.ts`)
export const XERO_BRAND_COLOR = '#1ab4d7'

export type XeroConnectionDetailsPayload = {
  id: string
  createdAt: string
  updatedAt: string
  refreshTokenStatus: XeroRefreshTokenStatus
  refreshTokenExpiration: string | null
}

/**
 * Flat payload used by the Xero entity-mapping UI.
 * Each row represents one AWX entity + vessel + OPERATIONS connected account combination.
 */
export type XeroEntityVesselPayload = {
  entityId: string
  entityName: string
  vesselId: string
  vesselName: string
  connectedAccountId: string
  connectedAccountName: string
}

/**
 * Defines a mapping between an OPS connected account
 * and a Xero ORGANISATION Tenant
 */
export type XeroConnectionsOrgMapping = {
  id: string
  xeroConnectionId: string
  awxConnectedAccountId: string
  // a.k.a. Xero Tenant ID
  xeroOrgId: string
  // a.k.a. Xero Tenant Name
  xeroOrgName: string
  // Xero's short organisation code, used to build manual-upload URLs.
  // Null until create/remap or the user presses Refresh connection.
  // Optional (not required) because apps/web still constructs this wire type without the field
  // in some fixtures; CreateXeroConnectionOrgMappingArgs (backend-internal) stays required.
  xeroOrgShortCode?: string | null
  updatedAt: string
}

/** @deprecated Removed with `XeroConnectionTaxRateMapping`; kept until frontend is rewired. */
export type XeroConnectionsTaxRateMapping = {
  id: string
  xeroConnectionOrgMappingId: string
  awxConnectedAccountId: string
  /** Xero's stable internal identifier for the tax rate (e.g. "INPUT2"). Used when posting transactions to Xero. */
  xeroTaxRateCode: string
  /** Human-readable display name of the tax rate in Xero (e.g. "15% GST on Expenses"). User-editable in Xero, varies per org. */
  xeroTaxRateName: string
  vatCode: VatCode
  updatedAt: string
}

export type XeroConnectionsCurrencyBankMapping = {
  id: string
  xeroConnectionOrgMappingId: string
  awxConnectedAccountId: string
  currencyCode: string
  xeroBankAccountId: string
  xeroBankAccountName: string
  updatedAt: string
}

/**
 * Ordered sequence of stages for a Xero connection sync.
 */
export enum XeroConnectionSyncStage {
  /**
   * loads the transactions to sync either using the pipeline args or the most recent 1k from the db
   */
  LOADING_TRANSACTIONS = 'LOADING_TRANSACTIONS',
  /**
   * Maps transactions to xero bank transactions
   */
  MAPPING = 'MAPPING',
  /**
   * syncs the mapped transactions to xero
   */
  SYNCING_TRANSACTIONS = 'SYNCING_TRANSACTIONS',
  /**
   * Syncs all the attachements for a transaction to a xero bank transaction
   */
  SYNCING_ATTACHMENTS = 'SYNCING_ATTACHMENTS',
}

export type XeroConnectionsSync = {
  id: string
  xeroConnectionId: string
  status: XeroConnectionSyncStatus
  createdAt: string
  finishedAt: string
  requiredStages: XeroConnectionSyncStage[]
  xeroConnectionOrgMappingId: string
  runId?: string
}

/** Lighthouse payload for a Xero sync job with resolved vessel/tenant context. */
export type XeroSyncJobPayload = {
  id: string
  status: XeroConnectionSyncStatus
  connectedAccountName: string
  tenantName: string
  startedAt: string
  finishedAt?: string
  duration?: number
  requiredStages: XeroConnectionSyncStage[]
  lastRun?: XeroSyncJobLastRun
}

/** Lighthouse payload for a single pipeline run within a sync job. */
export type XeroSyncJobRunPayload = {
  id: string
  status: string
  createdAt: string
  completedAt?: string
  type: string
}

/** Lighthouse payload for a single pipeline run with full stage data. */
export type XeroSyncJobRunDetailPayload = {
  id: string
  status: string
  createdAt: string
  completedAt?: string
  type: string
  data: XeroPipelineData
}

/** Lighthouse payload for a single run step. */
export type XeroSyncJobRunStepPayload = {
  id: string
  runId: string
  type: string
  status: string
  createdAt: string
  completedAt?: string
  data: Record<string, unknown>
}

/** Last run summary included in sync job listings. */
export type XeroSyncJobLastRun = {
  id: string
  status: string
  createdAt: string
  completedAt?: string
}

export enum XeroConnectionSyncStatus {
  PARTIAL = 'PARTIAL',
  NOT_STARTED = 'NOT_STARTED',
  IN_PROGRESS = 'IN_PROGRESS',
  COMPLETED = 'COMPLETED',
  FAILED = 'FAILED',
}

export type XeroConnectionsSyncedTransaction = {
  transactionId: string
  xeroConnectionSyncId: string
  status: XeroConnectionSyncStatus
  updatedAt: string
  finishedAt: string
  payload: { [key: string]: unknown }
  xeroTransactionId?: string
  xeroBankTransactionUrl?: string
}

export type XeroConnectionsSyncLog = {
  attemptId: string
  transactionId: string
  requestHeaders: { [key: string]: string }
  requestBody: { [key: string]: unknown }
  responseHeaders: { [key: string]: string }
  responseBody: { [key: string]: unknown }
  failureReason?: string
  metadata?: Record<string, unknown>
  createdAt: string
  status: XeroConnectionSyncStatus
  syncStage: XeroConnectionSyncStage
}
/**
 * Maps XeroConnectionSyncStage to the run step type used in the API query.
 */
export const XERO_STAGE_TO_STEP_TYPE: Record<XeroConnectionSyncStage, string> = {
  [XeroConnectionSyncStage.LOADING_TRANSACTIONS]: 'LOAD_TRANSACTIONS',
  [XeroConnectionSyncStage.MAPPING]: 'MAP_TRANSACTIONS',
  [XeroConnectionSyncStage.SYNCING_TRANSACTIONS]: 'SYNC_TRANSACTIONS',
  [XeroConnectionSyncStage.SYNCING_ATTACHMENTS]: 'SYNC_ATTACHMENTS',
}

/**
 * End raw xero types
 */

/**
 * Start gateway request/args types
 */

export type UpsertXeroConnectionArgs = {
  id: string
  organizationId: string
  accessToken: string
  refreshToken: string
  refreshTokenExpiration: string
  tokenExpiration: string
  status: XeroConnectionStatus
  createdAt?: string
  updatedAt?: string
}

export type ListXeroConnectionsArgs = {
  organizationId: string
  take?: number
  skip?: number
}

/**
 * Flips every Xero connection an organization owns to one status.
 *
 * Xero allows a single connection per organization, but the update is expressed as a bulk write
 * so the caller does not have to read the connection first.
 */
export type UpdateXeroConnectionsStatusArgs = {
  organizationId: string
  status: XeroConnectionStatus
}

export type CreateXeroConnectionOrgMappingArgs = {
  id: string
  xeroConnectionId: string
  awxConnectedAccountId: string
  xeroOrgId: string
  xeroOrgName: string
  xeroOrgShortCode: string | null
}

/**
 * Args for updating a Xero connection org mapping.
 * Supports both a full remap to a different Xero Org and a name refresh for the same Xero Org.
 */
export type UpdateXeroConnectionOrgMappingArgs = {
  id: string
  awxConnectedAccountId?: string
  xeroOrgId?: string
  xeroOrgName?: string
  xeroOrgShortCode?: string | null
}

export type ListXeroConnectionOrgMappingArgs = {
  organizationId: string
  take?: number
  skip?: number
}

/** @deprecated Removed with `XeroConnectionTaxRateMapping`; kept until frontend is rewired. */
export type CreateXeroTaxRateMappingArgs = {
  id: string
  xeroConnectionOrgMappingId: string
  awxConnectedAccountId: string
  /** Xero's stable internal identifier for the tax rate (e.g. "INPUT2"). */
  xeroTaxRateCode: string
  /** Human-readable display name from Xero (e.g. "15% GST on Expenses"). Stored for display purposes — user-editable in Xero, varies per org. */
  xeroTaxRateName: string
  vatCode: string
}

/**
 * Args for updating a Xero tax rate mapping.
 * `vatCode` is intentionally excluded as it identifies which
 * AnkorPay VAT category this mapping belongs to and should be immutable.
 * @deprecated Removed with `XeroConnectionTaxRateMapping`; kept until frontend is rewired.
 */
export type UpdateXeroTaxRateMappingArgs = {
  id: string
  /** Xero's stable internal identifier for the tax rate (e.g. "INPUT2"). */
  xeroTaxRateCode: string
  /** Human-readable display name from Xero (e.g. "15% GST on Expenses"). Stored for display purposes — user-editable in Xero, varies per org. */
  xeroTaxRateName: string
}

/** @deprecated Removed with `XeroConnectionTaxRateMapping`; kept until frontend is rewired. */
export type ListXeroTaxRateMappingArgs = {
  organizationId: string
  awxConnectedAccountId?: string
  take?: number
  skip?: number
}

export type CreateXeroCurrencyBankMappingArgs = {
  id: string
  xeroConnectionOrgMappingId: string
  awxConnectedAccountId: string
  currencyCode: string
  xeroBankAccountId: string
  xeroBankAccountName: string
}

/**
 * Args for updating the Xero bank account mapped to an AnkorPay wallet currency.
 * `currencyCode` is intentionally excluded as it identifies which
 * AnkorPay wallet this mapping belongs to and should be immutable.
 * The replacement Xero bank account must be in the same currency.
 */
export type UpdateXeroCurrencyBankMappingArgs = {
  id: string
  xeroBankAccountId: string
  xeroBankAccountName: string
}

export type ListXeroCurrencyBankMappingArgs = {
  organizationId: string
  awxConnectedAccountId?: string
  take?: number
  skip?: number
}

export type CreateXeroConnectionSyncArgs = {
  id: string
  xeroConnectionId: string
  xeroConnectionOrgMappingId: string
  status: XeroConnectionSyncStatus
  requiredStages: XeroConnectionSyncStage[]
}

export type UpdateXeroConnectionSyncArgs = {
  id: string
  status?: XeroConnectionSyncStatus
  finishedAt?: string
}

export type ListXeroConnectionSyncArgs = {
  xeroConnectionId: string
  connectedAccountName?: string
  take?: number
  skip?: number
}

export type CreateXeroConnectionSyncedTransactionItem = {
  transactionId: string
  xeroConnectionSyncId: string
  status: XeroConnectionSyncStatus
  payload: { [key: string]: unknown }
  xeroTransactionId?: string
  xeroBankTransactionUrl?: string
}

export type CreateXeroConnectionSyncedTransactionsArgs = {
  items: CreateXeroConnectionSyncedTransactionItem[]
}

export type UpdateXeroConnectionSyncedTransactionsArgs = {
  xeroConnectionSyncId: string
  transactionIds: string[]
  status?: XeroConnectionSyncStatus
  finishedAt?: string
  xeroTransactionId?: string
  xeroBankTransactionUrl?: string
}

export type ListXeroConnectionSyncedTransactionsArgs = {
  xeroConnectionSyncId: string
  take?: number
  skip?: number
}

export type CreateXeroConnectionSyncLogItem = {
  attemptId: string
  transactionId: string
  requestHeaders: { [key: string]: string }
  requestBody: { [key: string]: unknown }
  responseHeaders: { [key: string]: string }
  responseBody: { [key: string]: unknown }
  failureReason?: string
  metadata?: Record<string, unknown>
  status: XeroConnectionSyncStatus
  syncStage: XeroConnectionSyncStage
}

export type CreateXeroConnectionSyncLogsArgs = {
  items: CreateXeroConnectionSyncLogItem[]
}

export type UpdateXeroConnectionSyncLogsArgs = {
  transactionId: string
  attemptIds: string[]
  requestHeaders?: { [key: string]: string }
  requestBody?: { [key: string]: unknown }
  responseHeaders?: { [key: string]: string }
  responseBody?: { [key: string]: unknown }
  status?: XeroConnectionSyncStatus
  syncStage?: XeroConnectionSyncStage
  failureReason?: string
  metadata?: Record<string, unknown>
}

export type ListXeroConnectionSyncLogsArgs = {
  transactionId: string
  syncStage?: XeroConnectionSyncStage
  take?: number
  skip?: number
}

export type GetXeroConnectionSyncStateArgs = {
  xeroConnectionSyncId: string
}

export type XeroConnectionSyncState = {
  xeroConnectionSyncId: string
  transactions: {
    id: string
    xeroTransactionId?: string
    xeroBankTransactionUrl?: string
    status: XeroConnectionSyncStatus
    updatedAt: string
    finishedAt?: string
    payload: { [key: string]: unknown }
    stages: {
      [XeroConnectionSyncStage.LOADING_TRANSACTIONS]?: {
        attemptId: string
        requestHeaders: { [key: string]: string }
        requestBody: { [key: string]: unknown }
        responseHeaders: { [key: string]: string }
        responseBody: { [key: string]: unknown }
        status: XeroConnectionSyncStatus
        failureReason?: string
        metadata: Record<string, unknown>
        createdAt: string
      }
      [XeroConnectionSyncStage.MAPPING]?: {
        attemptId: string
        requestHeaders: { [key: string]: string }
        requestBody: { [key: string]: unknown }
        responseHeaders: { [key: string]: string }
        responseBody: { [key: string]: unknown }
        status: XeroConnectionSyncStatus
        metadata: Record<string, unknown>
        failureReason?: string
        createdAt: string
      }
      [XeroConnectionSyncStage.SYNCING_TRANSACTIONS]?: {
        attemptId: string
        requestHeaders: { [key: string]: string }
        requestBody: { [key: string]: unknown }
        responseHeaders: { [key: string]: string }
        responseBody: { [key: string]: unknown }
        status: XeroConnectionSyncStatus
        metadata: Record<string, unknown>
        failureReason?: string
        createdAt: string
      }
      [XeroConnectionSyncStage.SYNCING_ATTACHMENTS]?: {
        attemptId: string
        requestHeaders: { [key: string]: string }
        requestBody: { [key: string]: unknown }
        responseHeaders: { [key: string]: string }
        responseBody: { [key: string]: unknown }
        status: XeroConnectionSyncStatus
        failureReason?: string
        metadata: Record<string, unknown>
        createdAt: string
      }
    }
  }[]
}

export type CreateXeroCurrencyBankMappingItem = {
  xeroConnectionOrgMappingId: string
  currencyCode: string
  xeroBankAccountId: string
}

export type CreateXeroCurrencyBankMappingsBody = {
  items: CreateXeroCurrencyBankMappingItem[]
}

/**
 * Request item for updating the Xero bank account mapped to an AnkorPay wallet currency.
 */
export type UpdateXeroCurrencyBankMappingItem = {
  id: string
  xeroBankAccountId: string
}

export type UpdateXeroCurrencyBankMappingsBody = {
  items: UpdateXeroCurrencyBankMappingItem[]
}

/** @deprecated Removed with `XeroConnectionTaxRateMapping`; kept until frontend is rewired. */
export type CreateXeroTaxRateMappingItem = {
  xeroConnectionOrgMappingId: string
  /** Xero's stable internal identifier for the tax rate (e.g. "INPUT2"). The display name is resolved server-side from the Xero API. */
  xeroTaxRateCode: string
  vatCode: string
}

/** @deprecated Removed with `XeroConnectionTaxRateMapping`; kept until frontend is rewired. */
export type CreateXeroTaxRateMappingsBody = {
  items: CreateXeroTaxRateMappingItem[]
}

/**
 * Request item for updating the Xero tax rate mapped to an AnkorPay VAT code.
 * `vatCode` is intentionally excluded — it is immutable.
 * @deprecated Removed with `XeroConnectionTaxRateMapping`; kept until frontend is rewired.
 */
export type UpdateXeroTaxRateMappingItem = {
  id: string
  /** Xero's stable internal identifier for the tax rate (e.g. "INPUT2"). The display name is resolved server-side from the Xero API. */
  xeroTaxRateCode: string
}

/** @deprecated Removed with `XeroConnectionTaxRateMapping`; kept until frontend is rewired. */
export type UpdateXeroTaxRateMappingsBody = {
  items: UpdateXeroTaxRateMappingItem[]
}

export type CreateXeroOrgMappingItem = {
  awxConnectedAccountId: string
  xeroOrgId: string
  xeroOrgName: string
}

export type CreateXeroOrgMappingsBody = {
  items: CreateXeroOrgMappingItem[]
}

export type UpdateXeroOrgMappingItem = {
  id: string
  /** Xero tenant identity. Must always be updated together with xeroOrgName as a semantic pair. */
  xeroOrgId: string
  /** Human-readable Xero tenant name for the same xeroOrgId. Must always be updated together with xeroOrgId. */
  xeroOrgName: string
}

export type UpdateXeroOrgMappingsBody = {
  items: UpdateXeroOrgMappingItem[]
}

// TODO: Do we really need this additional type?
export type XeroOrgMappingPayload = XeroConnectionsOrgMapping

// TODO: Do we really need this additional type?
export type XeroTenantPayload = XeroApiTenant
/**
 * End gateway request/args types
 */

/**
 * Start Xero API response types
 * These map to the raw JSON shapes returned by the Xero REST API.
 */

/**
 * These are the possible tenant types from the Connections API.
 * For AnkorPay, we only care about ORGANISATION tenants.
 * PRACTICEMANAGER and PRACTICE are valid Xero values, but we dont use these at all.
 */
export enum XeroTenantType {
  /** Xero Accounting organisation tenant*/
  ORGANISATION = 'ORGANISATION',
  /** Xero Practice Manager tenant (valid Xero value, currently not supported by AnkorPay). */
  PRACTICEMANAGER = 'PRACTICEMANAGER',
  /** Xero HQ Practice tenant (valid Xero value, currently not supported by AnkorPay). */
  PRACTICE = 'PRACTICE',
}

/**
 * For our purposes, we consider this as a Xero Organization.
 * This is what we map to a connectedAccount.
 */
export type XeroApiTenant = {
  /** The ID for this connection */
  id: string
  /** The authentication event ID that connected this tenant. */
  authEventId: string
  /** The ID for the tenant that is connected. */
  tenantId: string
  /** The type of tenant */
  tenantType: XeroTenantType
  /** The name of the tenant (if it has one) */
  tenantName?: string
  /** The time when the connection to this tenant was created (ie. the first time it was authorised). */
  createdDateUtc: string
  /** The last time the user re-authorised the connection with this tenant and AnkorPay. */
  updatedDateUtc: string
}

export enum XeroApiAccountStatus {
  ACTIVE = 'ACTIVE',
  ARCHIVED = 'ARCHIVED',
  DELETED = 'DELETED',
}

/**
 * See https://developer.xero.com/documentation/api/accounting/types#tax-status-codes
 */
export enum XeroApiTaxStatus {
  /** The tax rate is active and can be used in transactions */
  ACTIVE = 'ACTIVE',
  /** The tax rate is deleted and cannot be restored or used on transactions */
  DELETED = 'DELETED',
  /**
   * The tax rate has been used on a transaction (e.g. an invoice) but has since been deleted.
   * ARCHIVED tax rates cannot be restored or used on transactions.
   */
  ARCHIVED = 'ARCHIVED',
}

export enum XeroApiAccountType {
  BANK = 'BANK',
  CURRENT = 'CURRENT',
  CURRLIAB = 'CURRLIAB',
  DEPRECIATN = 'DEPRECIATN',
  DIRECTCOSTS = 'DIRECTCOSTS',
  EQUITY = 'EQUITY',
  EXPENSE = 'EXPENSE',
  FIXED = 'FIXED',
  INVENTORY = 'INVENTORY',
  OTHERINCOME = 'OTHERINCOME',
  OVERHEADS = 'OVERHEADS',
  PREPAYMENT = 'PREPAYMENT',
  REVENUE = 'REVENUE',
  TERMLIAB = 'TERMLIAB',
}

export type XeroApiAccount = {
  /** Xero identifier (unique within organisations) */
  AccountID: string
  /** Customer defined alpha numeric account code e.g 200 or SALES */
  Code?: string
  /** Name of account */
  Name: string
  /** Accounts with a status of ACTIVE can be updated to ARCHIVED. See Account Status Codes */
  Status: XeroApiAccountStatus
  /** See Account Types */
  Type: XeroApiAccountType
  /** See Tax Types */
  TaxType: string
  /** Description of the Account. Valid for all types of accounts except bank accounts */
  Description?: string
  /** See Account Class Types */
  Class: string
  /** If this is a system account then this element is returned. See System Account types. Note that non-system accounts may have this element set as either "" or null. */
  SystemAccount?: string
  /** Boolean – describes whether account can have payments applied to it */
  EnablePaymentsToAccount: boolean
  /** Boolean – describes whether account code is available for use with expense claims */
  ShowInExpenseClaims: boolean
  /** For bank accounts only (Account Type BANK) */
  BankAccountNumber?: string
  /** For bank accounts only. See Bank Account types */
  BankAccountType?: string
  /** For bank accounts only */
  CurrencyCode?: string
  /** Shown if set */
  ReportingCode?: string
  /** Shown if set */
  ReportingCodeName?: string
  /** boolean to indicate if an account has an attachment */
  HasAttachments: boolean
  /** Last modified date UTC format */
  UpdatedDateUTC: string
  /** Boolean that determines if this account is shown in the Xero dashboard watchlist widget */
  AddToWatchlist: boolean
}

/**
 * Filter options for querying Xero accounts via the Accounts API.
 * Maps to Xero's `where` query parameter using their expression syntax.
 *
 * https://developer.xero.com/documentation/api/accounting/accounts#get-accounts
 */
export type XeroAccountsFilter = {
  /** Filter by account type (e.g. BANK, EXPENSE). Maps to Type=="value". */
  type?: XeroApiAccountType
  /** Exclude accounts of this type. Maps to Type!="value". */
  typeNotEqual?: XeroApiAccountType
  /** Filter by account status. Defaults to returning all statuses if omitted. */
  status?: XeroApiAccountStatus
}

export type XeroApiAccountsResponse = {
  Id: string
  Status: string
  ProviderName: string
  DateTimeUTC: string
  Accounts: XeroApiAccount[]
}

export type XeroApiOrganisation = {
  OrganisationID: string
  ShortCode: string
}

export type XeroApiOrganisationsResponse = {
  Organisations: XeroApiOrganisation[]
}

export type XeroApiTaxComponent = {
  Name: string
  Rate: number
  IsCompound: boolean
  IsNonRecoverable: boolean
}

export type XeroApiTaxRate = {
  Name: string
  TaxType: string
  TaxComponents: XeroApiTaxComponent[]
  Status: XeroApiTaxStatus
  /** See https://developer.xero.com/documentation/api/accounting/types#report-tax-types */
  ReportTaxType: string
  CanApplyToAssets: boolean
  CanApplyToEquity: boolean
  CanApplyToExpenses: boolean
  CanApplyToLiabilities: boolean
  CanApplyToRevenue: boolean
  DisplayTaxRate: number
  EffectiveRate: number
}

export type XeroApiTaxRatesResponse = {
  Id: string
  Status: string
  ProviderName: string
  DateTimeUTC: string
  TaxRates: XeroApiTaxRate[]
}

/**
 * Tracking entry for a BankTransaction LineItem.
 * Both IDs must be the original Xero UUIDs (not our internal deterministic UUIDs).
 */
export type XeroApiBankTransactionLineItemTracking = {
  TrackingCategoryID: string
  TrackingOptionID: string
  /** Category display name in Xero. */
  Name?: string
  /** Selected option display name in Xero. */
  Option?: string
}

export type XeroApiBankTransactionLineItem = {
  description: string
  quantity: number // float
  unitAmount: number // float
  accountCode: string
  /** Omitted when the transaction's LineAmountTypes is NoTax */
  TaxType?: string
  Tracking?: XeroApiBankTransactionLineItemTracking[]
}

export type XeroApiBankTransactionBankAccount = {
  AccountID: string
}

/** Xero's LineAmountTypes: how line amounts carry tax. Only sent for NoTax, otherwise Xero applies its own default */
export enum XeroApiLineAmountTypes {
  EXCLUSIVE = 'Exclusive',
  INCLUSIVE = 'Inclusive',
  NO_TAX = 'NoTax',
}

export enum XeroApiBankTransactionType {
  RECEIVE = 'RECEIVE',
  RECEIVE_OVERPAYMENT = 'RECEIVE-OVERPAYMENT',
  RECEIVE_PREPAYMENT = 'RECEIVE-PREPAYMENT',
  SPEND = 'SPEND',
  SPEND_OVERPAYMENT = 'SPEND-OVERPAYMENT',
  SPEND_PREPAYMENT = 'SPEND-PREPAYMENT',
  /** Supported by Xero for GET responses, not for create/update operations. */
  RECEIVE_TRANSFER = 'RECEIVE-TRANSFER',
  /** Supported by Xero for GET responses, not for create/update operations. */
  SPEND_TRANSFER = 'SPEND-TRANSFER',
}

export type XeroApiBankTransactionContact = {
  name: string
}

/**
 * This represents a "Transaction/Expense" within AnkorPay
 */
export type XeroApiBankTransaction = {
  /** For most (if not all) cases the type will be "SPEND" */
  type: XeroApiBankTransactionType
  contact: XeroApiBankTransactionContact
  /** When creating from AnkorPay, we *only* ever have 1 line item here, representing the transaction itself. */
  lineItems: XeroApiBankTransactionLineItem[]
  bankAccount: XeroApiBankTransactionBankAccount
  // we pass our own reference to the transaction
  Reference: string
  LineAmountTypes?: XeroApiLineAmountTypes
}

export type XeroApiBankTransactionsRequest = {
  BankTransactions: XeroApiBankTransaction[]
}

export type XeroApiBankTransactionResponse = {
  Id: string
  Status: string
  ProviderName: string
  DateTimeUTC: string
  BankTransactions: (XeroApiBankTransaction & {
    BankTransactionID: string
    IsReconciled: boolean
    HasAttachments: boolean
  })[]
}

/**
 * End Xero API response types
 */

export type XeroOAuthParams = {
  response_type: 'code'
  client_id: string
  redirect_uri: string
  scope: string
  state: string
}

export type XeroCallbackQuery = {
  code: string
  state: string
}

export type XeroTokenRequestBody = {
  grant_type: 'authorization_code'
  code: string
  redirect_uri: string
}

export type XeroTokenResponse = {
  access_token: string
  refresh_token: string
  // always 1800 (30 min)
  expires_in: number
  token_type: 'Bearer'
  scope: string
  id_token?: string
}

export type XeroTokenErrorResponse = {
  error: string
  error_description?: string
}

export enum XeroMappingType {
  CHART_OF_ACCOUNTS = 'CHART_OF_ACCOUNTS',
  TAX_RATES = 'TAX_RATES',
  CURRENCY_BANK = 'CURRENCY_BANK',
  TRACKING_CATEGORIES = 'TRACKING_CATEGORIES',
}

/**
 * Represents a xero mapping item for the UI to use in the listing and editing of xero mappings.
 */
export type XeroMappingItem<T> = {
  // ankor pay information
  ankorPay: {
    // the ankor pay organization id mapped to this xero mapping item (optional)
    organizationId?: string
    // the ankor pay vessel name associated with the connected account (required for display purposes)
    vesselName: string
    // the ankor pay entity associated with the connected account
    entityId: string
    // the ankor pay entity name associated with the connected account (required for display purposes)
    entityName: string
    // the ankor pay connected account name (required for display purposes)
    awxConnectedAccountName: string
    // the ankor pay connected account id associated with the connected account
    awxConnectedAccountId: string
  }
  // xero mapping information
  xero: {
    // the id of the xero connection org mapping associated with the connected account
    connectionOrgMappingId: string
    // the xero org id (optional)
    orgId?: string
    // the xero org name (required for display purposes)
    orgName: string
  }
  type: XeroMappingType
  // The actual items to be mapped (e.g. currency, tax rate, chart of accounts)
  items: T[]
}

export type XeroMappingChartOfAccountsItem = {
  account: {
    id: string
    code: string
    name: string
    type: XeroApiAccountType
    description?: string
  }
  // whether the account is selected for mapping
  selected: boolean
  // the corresponding stored category in the db
  category?: CategoryPayload
}

export type XeroTaxRateImportItem = {
  taxRate: {
    /** This is essentially an id that is unique within a xero org */
    taxType: string
    /** Live name in Xero */
    name: string
    /** We include this in case the UI needs to display the tax rate percentage */
    displayTaxRate: number
  }
  /** Can be derived from stored TaxLabel.status === ACTIVE */
  selected: boolean
  /** The corresponding stored tax label in the db (if it exists) */
  taxLabel?: TaxLabelPayload
}

/**
 * The live AWX wallet balance for a currency plus DB mapping fields. \
 * `balance.currency` and `mapping.currencyCode` both represent the same currency.
 */
export type XeroCurrencyBankMappingItem = {
  /**
   * The balance record for this specific AnkorPay wallet currency
   */
  balance: Balance
  /**
   * The mapping between the AnkorPay currency wallet and the Xero Bank Account of the same currency.
   * Optional, as users may not always map all their AnkorPay wallet currencies to a Xero Bank Account
   */
  mapping?: XeroConnectionsCurrencyBankMapping
}

/**
 * Tracks the pipeline run state
 */
export type XeroPipelineData = {
  stages: {
    [XeroConnectionSyncStage.LOADING_TRANSACTIONS]: {
      status: XeroConnectionSyncStatus
      issue?: string
    }
    [XeroConnectionSyncStage.MAPPING]: {
      status: XeroConnectionSyncStatus
      issue?: string
    }
    [XeroConnectionSyncStage.SYNCING_TRANSACTIONS]: {
      status: XeroConnectionSyncStatus
      issue?: string
      syncedTransactions: {
        transactionId: string
        xeroBankTransactionId?: string
      }[]
    }
    [XeroConnectionSyncStage.SYNCING_ATTACHMENTS]: {
      status: XeroConnectionSyncStatus
      issue?: string
      syncedAttachments: {
        transactionId: string
        attachments: {
          id: string
          synced: boolean
        }[]
      }[]
    }
  }
}

export type XeroConnectionSyncRequest = {
  result: XeroConnectionSyncResult
  reason?: XeroConnectionSyncDeniedReason
  retryAfter?: number
}

export enum XeroConnectionSyncResult {
  ACCEPTED = 'ACCEPTED',
  DENIED = 'DENIED',
}

export enum XeroConnectionSyncDeniedReason {
  /**
   * The user hasn't granted the oauth flow to the xero connection
   */
  XERO_CONNECTION_NOT_FOUND = 'XERO_CONNECTION_NOT_FOUND',
  /**
   * The xero connection exists but has been deactivated for this organization
   */
  XERO_CONNECTION_INACTIVE = 'XERO_CONNECTION_INACTIVE',
  /**
   * The xero connection sync is not found
   */
  XERO_CONNECTION_SYNC_NOT_FOUND = 'XERO_CONNECTION_SYNC_NOT_FOUND',
  /**
   * The user hasn't mapped the connected account to a xero organization
   */
  ORGANIZATION_MAPPING_NOT_FOUND = 'ORGANIZATION_MAPPING_NOT_FOUND',
  /**
   * The xero connection sync pipeline is already running for this xero organization
   */
  PIPELINE_BUSY = 'PIPELINE_BUSY',
}

export type GetXeroConnectionByIdArgs = {
  id: string
}

export type GetXeroConnectionByOrgIdAndConnectedAccountIdArgs = {
  organizationId: string
  connectedAccountId: string
}

export type GetXeroConnectionOrgMappingArgs =
  | GetXeroConnectionByIdArgs
  | GetXeroConnectionByOrgIdAndConnectedAccountIdArgs

export type XeroIntegrationPipelinePayload = {
  pipelineId: string
  startedAt: string
  finishedAt?: string
  duration?: number
  status: XeroConnectionSyncStatus
  tenantName: string
  connectedAccountName: string
  stages: XeroIntegrationPipelineStagePayload[]
}

export type XeroIntegrationPipelineStagePayload = {
  stage: XeroConnectionSyncStage
  startedAt: string
  finishedAt?: string
  duration?: number
  status: XeroConnectionSyncStatus
}

export type XeroIntegrationPipelineTransactionPayload = {
  id: string
  xeroTransactionId?: string
  xeroBankTransactionUrl?: string
  status: XeroConnectionSyncStatus
  updatedAt: string
  finishedAt?: string
}

export type XeroIntegrationPipelineStageLogPayload = {
  attemptId: string
  transactionId: string
  responseHeaders: { [key: string]: string }
  responseBody: { [key: string]: unknown }
  status: XeroConnectionSyncStatus
  failureReason?: string
  metadata?: Record<string, unknown>
  createdAt: string
  syncStage: XeroConnectionSyncStage
}

/**
 * Mirrors the Status field on Xero TrackingCategory and TrackingOption objects.
 * DELETED is never returned by the Xero API — absence from the response is treated as DELETED.
 */
export enum XeroTrackingStatus {
  ACTIVE = 'ACTIVE',
  ARCHIVED = 'ARCHIVED',
  DELETED = 'DELETED',
}

/** Admin-configured state for a tracking category: controls visibility and whether selection is required. */
export enum XeroTrackingCategoryAdminState {
  DISABLED = 'DISABLED', // is neither enabled or mandatory
  OPTIONAL = 'OPTIONAL', // enabled (appears as a dropdown) but not mandatory
  REQUIRED = 'REQUIRED', // enabled and mandatory
}

/** Admin-configured state for a tracking option: controls whether it appears in the dropdown. */
export enum XeroTrackingOptionAdminState {
  DISABLED = 'DISABLED', // is not shown as a selectable option in the dropdown
  ENABLED = 'ENABLED', // is shown as a selectable option in the dropdown
}

/** A tracking category as returned directly by the Xero Accounting API. */
export type XeroApiTrackingCategory = {
  TrackingCategoryID: string
  Name: string
  Status: XeroTrackingStatus
  Options: XeroApiTrackingCategoryOption[]
}

/** A tracking option as returned directly by the Xero Accounting API. */
export type XeroApiTrackingCategoryOption = {
  TrackingOptionID: string
  Name: string
  Status: XeroTrackingStatus
}

/** Wrapper for the GET /TrackingCategories response envelope. */
export type XeroApiTrackingCategoriesResponse = {
  TrackingCategories: XeroApiTrackingCategory[]
}

/**
 * Internal domain representation of a Xero tracking category stored in AnkorPay.
 * `id` is our deterministic internal UUID; `xeroTrackingCategoryId` is the original Xero UUID.
 * `name` and `status` are kept in sync with Xero; `adminState` is admin-configured.
 */
export type XeroTrackingCategory = {
  /** Deterministic UUID v5 derived from (xeroTrackingCategoryId, xeroConnectionOrgMappingId). */
  id: string
  /** Original UUID assigned by Xero. Required when sending tracking data to the Xero API. */
  xeroTrackingCategoryId: string
  /** FK to XeroConnectionOrgMapping — identifies the Xero tenant this category belongs to. */
  xeroConnectionOrgMappingId: string
  /** Denormalized from XeroConnectionOrgMapping for sync pipeline filtering. */
  awxConnectedAccountId: string
  /** Xero's display name. */
  name: string
  /** Mirrors Xero status. */
  status: XeroTrackingStatus
  /** Admin-configured state: DISABLED (not shown), OPTIONAL (shown, not required), REQUIRED (shown, must select). */
  adminState: XeroTrackingCategoryAdminState
  options: XeroTrackingCategoryOption[]
}

/**
 * Internal domain representation of a Xero tracking option stored in AnkorPay.
 * `id` is our deterministic internal UUID; `xeroTrackingCategoryOptionId` is the original Xero UUID.
 */
export type XeroTrackingCategoryOption = {
  /** Deterministic UUID v5 derived from (xeroTrackingCategoryOptionId, xeroTrackingCategoryId). */
  id: string
  /** Original UUID assigned by Xero. Required when sending tracking data to the Xero API. */
  xeroTrackingCategoryOptionId: string
  /** Our internal XeroTrackingCategory.id (not the Xero UUID). */
  xeroTrackingCategoryId: string
  /** Xero's display name. Shown read-only in AnkorPay. */
  name: string
  /** Mirrors Xero status. */
  status: XeroTrackingStatus
  /** Admin-configured state: DISABLED (hidden from dropdown) or ENABLED (shown in dropdown). */
  adminState: XeroTrackingOptionAdminState
}

/** A single option to upsert as part of an admin save. */
export type UpsertXeroTrackingCategoryOptionRequest = {
  /** Deterministic UUID v5 — used as the upsert key. */
  id: string
  /** Original UUID assigned by Xero. */
  xeroTrackingCategoryOptionId: string
  /** Our internal XeroTrackingCategory.id (not the Xero UUID). */
  xeroTrackingCategoryId: string
  name: string
  status: XeroTrackingStatus
  adminState: XeroTrackingOptionAdminState
}

/** A single tracking category to upsert, including its options. */
export type UpsertXeroTrackingCategoryRequest = {
  /** Deterministic UUID v5 — used as the upsert key. */
  id: string
  /** Original UUID assigned by Xero. */
  xeroTrackingCategoryId: string
  xeroConnectionOrgMappingId: string
  awxConnectedAccountId: string
  name: string
  status: XeroTrackingStatus
  adminState: XeroTrackingCategoryAdminState
  options: UpsertXeroTrackingCategoryOptionRequest[]
}

/**
 * Args for bulk-upserting tracking categories and their options.
 * Called from the Save (PUT) path — the full snapshot from the request body is the source of truth.
 */
export type UpsertXeroTrackingCategoriesArgs = {
  trackingCategories: UpsertXeroTrackingCategoryRequest[]
}

/** Args for listing tracking categories belonging to an organization. */
export type ListXeroTrackingCategoriesArgs = {
  organizationId: string
  awxConnectedAccountId?: string
  /* If provided, only return tracking categories with the given admin states */
  categoryAdminStates?: XeroTrackingCategoryAdminState[]
  /* If provided, only return tracking options with the given admin states */
  optionAdminStates?: XeroTrackingOptionAdminState[]
  /* If provided, only return tracking categories and options with the given statuses */
  statuses?: XeroTrackingStatus[]
}

/**
 * Admin UI item for a single tracking category within a `XeroMappingItem<T>` payload.
 *
 * Each item carries two pairs of name/status fields to support drift detection:
 * - `name` / `status` — last-saved values from our DB (persisted on Save). Acts as a stable
 *   fallback label and a snapshot of what the admin last acknowledged.
 * - `xeroName` / `xeroStatus` — live values from the most recent Xero API call. Null when
 *   the item no longer exists in Xero (deleted items are omitted from the API response entirely).
 *
 * The client can diff the two pairs to surface drift (e.g. renamed, archived, deleted in Xero
 * since the last Save) and prompt the admin to re-save to bring the DB up to date.
 */
export type XeroMappingTrackingCategoryItem = {
  /** Our internal deterministic UUID. Null if this category has not yet been saved to our DB. */
  id: string | null
  /** Original UUID assigned by Xero. */
  xeroTrackingCategoryId: string
  /** Last-saved name from our DB. Falls back to the live Xero name if never saved. */
  name: string
  /** Last-saved status from our DB. Falls back to the live Xero status if never saved. */
  status: XeroTrackingStatus
  /** Live name from Xero. Null if this category was deleted in Xero (absent from API response). */
  xeroName: string | null
  /** Live status from Xero. Null if this category was deleted in Xero (absent from API response). */
  xeroStatus: XeroTrackingStatus | null
  /** Admin-configured state: DISABLED (not shown), OPTIONAL (shown, not required), REQUIRED (shown, must select). */
  adminState: XeroTrackingCategoryAdminState
  options: XeroMappingTrackingCategoryOptionItem[]
}

/**
 * Admin UI item for a single tracking option within a XeroMappingTrackingCategoryItem.
 *
 * Same dual name/status pattern as the parent category — see XeroMappingTrackingCategoryItem.
 */
export type XeroMappingTrackingCategoryOptionItem = {
  /** Our internal deterministic UUID. Null if not yet saved to our DB. */
  id: string | null
  /** Original UUID assigned by Xero. */
  xeroTrackingCategoryOptionId: string
  /** Last-saved name from our DB. Falls back to the live Xero name if never saved. */
  name: string
  /** Last-saved status from our DB. Falls back to the live Xero status if never saved. */
  status: XeroTrackingStatus
  /** Live name from Xero. Null if this option was deleted in Xero (absent from API response). */
  xeroName: string | null
  /** Live status from Xero. Null if this option was deleted in Xero (absent from API response). */
  xeroStatus: XeroTrackingStatus | null
  /** Admin-configured state: DISABLED (hidden from dropdown) or ENABLED (shown in dropdown). */
  adminState: XeroTrackingOptionAdminState
}

/**
 * Tracking selection included in TransactionPayload.
 * Reflects the tracking option assigned to the transaction at time of retrieval.
 * Names reflect current Xero values (not historic values at time of assignment).
 */
export type TransactionTrackingCategoryPayload = {
  trackingCategoryId: string
  /** Original Xero UUID for the category. Used when syncing to Xero API. */
  xeroTrackingCategoryId: string
  /** Current Xero name for the category. */
  trackingCategoryName: string
  /** Last-known Xero status for the category. */
  trackingCategoryStatus: XeroTrackingStatus
  trackingOptionId: string
  /** Original Xero UUID for the option. Used when syncing to Xero API. */
  xeroTrackingOptionId: string
  /** Current Xero name for the option. */
  trackingOptionName: string
  /** Last-known Xero status for the option. */
  trackingOptionStatus: XeroTrackingStatus
}

/**
 * Tracking selections provided by the client when creating or editing a transaction.
 * Uses internal XeroTrackingCategoryOption UUIDs — not Xero UUIDs.
 * Up to 2 entries (mirrors Xero's limit of 2 active tracking categories per org).
 * The category is not supplied directly — it is derived from the option via join.
 */
export type TransactionTrackingCategoryInput = {
  /** Our internal XeroTrackingCategoryOption.id for the first tracking slot. Null = no selection. */
  xeroTrackingOption1Id: string | null
  /** Our internal XeroTrackingCategoryOption.id for the second tracking slot. Null = no selection. */
  xeroTrackingOption2Id: string | null
}

export type XeroTrackingOptionPayload = {
  id: string
  name: string
}

export type TrackingCategoryWithOptionsPayload = {
  id: string
  name: string
  adminState: XeroTrackingCategoryAdminState
  options: XeroTrackingOptionPayload[]
}

export type XeroTrackingCategoriesPayload = {
  trackingCategories: TrackingCategoryWithOptionsPayload[]
}
